import React, { useState } from "react";
import moment from "moment";
import { Role } from "@teamrota/authlib";
import {
  Button,
  MenuItem,
  Select,
  Grid,
  CircularProgress,
  FormControl
} from "@material-ui/core";

import { RotaCalendar } from "@teamrota/rota-design";
import CustomDropzone from "~/src/components/drop-zone/drop-zone";
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPES_LABELS,
  MAX_UPLOAD_SIZE_MB,
  RIGHT_TO_WORK_SUBTYPES,
  RIGHT_TO_WORK_SUBTYPES_LABELS
} from "~/src/consts";

import ComplianceDocumentsTable from "./ComplianceDocumentsTable";
import useStyles, { StyledSentenceCase, StyledValidationError } from "./styles";
import useAuth from "~/src/auth/hooks/use-auth";

const COMPLIANCE_DOCUMENT_TYPES = [
  DOCUMENT_TYPES.RIGHT_TO_WORK,
  DOCUMENT_TYPES.COSHH,
  DOCUMENT_TYPES.DBS_BASIC,
  DOCUMENT_TYPES.DBS_ENHANCED,
  DOCUMENT_TYPES.ENHANCED_CHILD_DBS,
  DOCUMENT_TYPES.FIRE_SAFETY,
  DOCUMENT_TYPES.HACCP,
  DOCUMENT_TYPES.REFERENCE,
  DOCUMENT_TYPES.REFERENCE_2,
  DOCUMENT_TYPES.HEALTH_SAFETY_LEVEL_1,
  DOCUMENT_TYPES.HEALTH_SAFETY_LEVEL_2,
  DOCUMENT_TYPES.HEALTH_SAFETY_LEVEL_3,
  DOCUMENT_TYPES.ALLERGEN_AWARENESS,
  DOCUMENT_TYPES.FLOW_TRAINING,
  DOCUMENT_TYPES.PROOF_OF_ADDRESS,
  DOCUMENT_TYPES.NATIONAL_INSURANCE,
  DOCUMENT_TYPES.COVID_VACCINE_PASSPORT,
  DOCUMENT_TYPES.CV,
  DOCUMENT_TYPES.TRADE_TEST,
  DOCUMENT_TYPES.OTHER,
  DOCUMENT_TYPES.DEED_OF_NAME_CHANGE,
  DOCUMENT_TYPES.MARRIAGE_CERTIFICATE,
  DOCUMENT_TYPES.UNIVERSITY_ENROLMENT_LETTER,
  DOCUMENT_TYPES.UNIVERSITY_TERM_DATES,
  DOCUMENT_TYPES.HOME_OFFICE_LETTER,
  DOCUMENT_TYPES.SPOUSAL_DOCUMENT,
  DOCUMENT_TYPES.BIRTH_CERTIFICATE,
  DOCUMENT_TYPES.FOOD_SAFETY_LEVEL_1,
  DOCUMENT_TYPES.FOOD_SAFETY_LEVEL_2,
  DOCUMENT_TYPES.FOOD_SAFETY_LEVEL_3,
  DOCUMENT_TYPES.BOOKING_CONTRACT,
  DOCUMENT_TYPES.MANUAL_HANDLING,
  DOCUMENT_TYPES.SLIPS_TRIPS_FALLS_AWARENESS,
  DOCUMENT_TYPES.APPLICATION_FORM,
  DOCUMENT_TYPES.BASIC_FIRST_AID,
  DOCUMENT_TYPES.BUCCAL,
  DOCUMENT_TYPES.CHILD_PROTECTION,
  DOCUMENT_TYPES.CLEARANCE,
  DOCUMENT_TYPES.CORU,
  DOCUMENT_TYPES.CORU_PIN_CHECK,
  DOCUMENT_TYPES.DRIVERS_LICENSE,
  DOCUMENT_TYPES.EVIDENCE_OF_PPS,
  DOCUMENT_TYPES.FIRE_SAFETY_AWARENESS_ONLINE,
  DOCUMENT_TYPES.GARDA_VETTING_FW,
  DOCUMENT_TYPES.GARDA_VETTING_NFW,
  DOCUMENT_TYPES.GARDA_VETTING_APPLICATION_FORM,
  DOCUMENT_TYPES.GDPR,
  DOCUMENT_TYPES.HAND_HYGIENE,
  DOCUMENT_TYPES.HEALTHIER_BUSINESS_FORM,
  DOCUMENT_TYPES.HEARTSAVER_AED_CPR,
  DOCUMENT_TYPES.INFECTION_PREVENTION_CONTROL,
  DOCUMENT_TYPES.INTERVIEW_NOTES,
  DOCUMENT_TYPES.IRISH_MEDICAL_COUNCIL,
  DOCUMENT_TYPES.IRISH_MEDICAL_COUNCIL_PIN_CHECK,
  DOCUMENT_TYPES.MAPA,
  DOCUMENT_TYPES.MEDICATION_MANAGEMENT,
  DOCUMENT_TYPES.NMBI_PIN_CHECK,
  DOCUMENT_TYPES.NMBI_REGISTRATION,
  DOCUMENT_TYPES.OCCUPATIONAL_FIRST_AID,
  DOCUMENT_TYPES.OCCUPATIONAL_HEALTH,
  DOCUMENT_TYPES.PASSPORT_PHOTO,
  DOCUMENT_TYPES.PATIENT_MOVING_HANDLING,
  DOCUMENT_TYPES.QC_NOTE,
  DOCUMENT_TYPES.QUALIFICATIONS,
  DOCUMENT_TYPES.REFERENCE_3,
  DOCUMENT_TYPES.RISK_ASSESSMENT,
  DOCUMENT_TYPES.SAFE_ADMINISTRATION_OF_MEDICATION,
  DOCUMENT_TYPES.SAFEGUARDING_OF_VULNERABLE_ADULTS,
  DOCUMENT_TYPES.SIGN_OFF,
  DOCUMENT_TYPES.SKYPE_SCREENING_CALL,
  DOCUMENT_TYPES.TCI,
  DOCUMENT_TYPES.VACCINATIONS,
  DOCUMENT_TYPES.VERIFICATION_OF_SERVICE
];

const NO_ISSUE_DATE_NEEDED = [
  DOCUMENT_TYPES.REFERENCE,
  DOCUMENT_TYPES.NATIONAL_INSURANCE,
  DOCUMENT_TYPES.CV,
  RIGHT_TO_WORK_SUBTYPES.STUDENT_VISA_10_HOURS,
  RIGHT_TO_WORK_SUBTYPES.STUDENT_VISA_20_HOURS,
  DOCUMENT_TYPES.TRADE_TEST,
  DOCUMENT_TYPES.OTHER,
  DOCUMENT_TYPES.DEED_OF_NAME_CHANGE,
  DOCUMENT_TYPES.MARRIAGE_CERTIFICATE,
  DOCUMENT_TYPES.UNIVERSITY_ENROLMENT_LETTER,
  DOCUMENT_TYPES.UNIVERSITY_TERM_DATES,
  DOCUMENT_TYPES.HOME_OFFICE_LETTER,
  DOCUMENT_TYPES.SPOUSAL_DOCUMENT,
  RIGHT_TO_WORK_SUBTYPES.EEA_SETTLEMENT_STATUS_REPORT,
  RIGHT_TO_WORK_SUBTYPES.EEA_SETTLEMENT_STATUS_REPORT_SETTLED,
  RIGHT_TO_WORK_SUBTYPES.PASSPORT_WITH_RIGHT_OF_RESIDENCE_EEA_NATIONAL_FAMILY_MEMBER,
  RIGHT_TO_WORK_SUBTYPES.PASSPORT_WITH_VISA_GIVING_INDEFINITE_LEAVE_TO_REMAIN,
  RIGHT_TO_WORK_SUBTYPES.PASSPORT_WITH_VISA_GIVING_LIMITED_LEAVE_TO_REMAIN,
  DOCUMENT_TYPES.COSHH,
  DOCUMENT_TYPES.FIRE_SAFETY,
  DOCUMENT_TYPES.HACCP,
  DOCUMENT_TYPES.SLIPS_TRIPS_FALLS_AWARENESS,
  DOCUMENT_TYPES.APPLICATION_FORM,
  DOCUMENT_TYPES.CLEARANCE,
  DOCUMENT_TYPES.CORU_PIN_CHECK,
  DOCUMENT_TYPES.EVIDENCE_OF_PPS,
  DOCUMENT_TYPES.GARDA_VETTING_APPLICATION_FORM,
  DOCUMENT_TYPES.HEALTHIER_BUSINESS_FORM,
  DOCUMENT_TYPES.INTERVIEW_NOTES,
  DOCUMENT_TYPES.IRISH_MEDICAL_COUNCIL_PIN_CHECK,
  DOCUMENT_TYPES.NMBI_PIN_CHECK,
  DOCUMENT_TYPES.PASSPORT_PHOTO,
  DOCUMENT_TYPES.QC_NOTE,
  DOCUMENT_TYPES.QUALIFICATIONS,
  DOCUMENT_TYPES.REFERENCE_3,
  DOCUMENT_TYPES.RISK_ASSESSMENT,
  DOCUMENT_TYPES.SIGN_OFF,
  DOCUMENT_TYPES.SKYPE_SCREENING_CALL,
  DOCUMENT_TYPES.VACCINATIONS,
  DOCUMENT_TYPES.VERIFICATION_OF_SERVICE
];

const NO_EXPIRY_DATE_NEEDED = [
  RIGHT_TO_WORK_SUBTYPES.FULL_BIRTH_CERTIFICATE,
  RIGHT_TO_WORK_SUBTYPES.CERTIFICATE_OF_NATURALISATION,
  RIGHT_TO_WORK_SUBTYPES.IMMIGRATION_STATUS_DOCUMENT,
  RIGHT_TO_WORK_SUBTYPES.EEA_SETTLEMENT_STATUS_REPORT_SETTLED,
  DOCUMENT_TYPES.REFERENCE,
  DOCUMENT_TYPES.FLOW_TRAINING,
  DOCUMENT_TYPES.PROOF_OF_ADDRESS,
  DOCUMENT_TYPES.NATIONAL_INSURANCE,
  DOCUMENT_TYPES.CV,
  DOCUMENT_TYPES.TRADE_TEST,
  DOCUMENT_TYPES.OTHER,
  DOCUMENT_TYPES.DEED_OF_NAME_CHANGE,
  DOCUMENT_TYPES.MARRIAGE_CERTIFICATE,
  DOCUMENT_TYPES.UNIVERSITY_ENROLMENT_LETTER,
  DOCUMENT_TYPES.UNIVERSITY_TERM_DATES,
  DOCUMENT_TYPES.HOME_OFFICE_LETTER,
  DOCUMENT_TYPES.SPOUSAL_DOCUMENT,
  DOCUMENT_TYPES.BIRTH_CERTIFICATE,
  DOCUMENT_TYPES.APPLICATION_FORM,
  DOCUMENT_TYPES.CLEARANCE,
  DOCUMENT_TYPES.CORU_PIN_CHECK,
  DOCUMENT_TYPES.EVIDENCE_OF_PPS,
  DOCUMENT_TYPES.GARDA_VETTING_APPLICATION_FORM,
  DOCUMENT_TYPES.HEALTHIER_BUSINESS_FORM,
  DOCUMENT_TYPES.INTERVIEW_NOTES,
  DOCUMENT_TYPES.IRISH_MEDICAL_COUNCIL_PIN_CHECK,
  DOCUMENT_TYPES.NMBI_PIN_CHECK,
  DOCUMENT_TYPES.PASSPORT_PHOTO,
  DOCUMENT_TYPES.QC_NOTE,
  DOCUMENT_TYPES.QUALIFICATIONS,
  DOCUMENT_TYPES.REFERENCE_3,
  DOCUMENT_TYPES.RISK_ASSESSMENT,
  DOCUMENT_TYPES.SIGN_OFF,
  DOCUMENT_TYPES.SKYPE_SCREENING_CALL,
  DOCUMENT_TYPES.VACCINATIONS,
  DOCUMENT_TYPES.VERIFICATION_OF_SERVICE
];

const ComplianceDocuments = ({
  handleUploadFile,
  complianceDocuments,
  handleDeleteDocument,
  uploadPercentage,
  setUploadPercentage
}) => {
  const classes = useStyles();

  const [documentType, setDocumentType] = useState("");
  const [documentSubtype, setDocumentSubtype] = useState("");
  const [documentIssueDate, setDocumentIssueDate] = useState(new Date());
  const [documentExpiryDate, setDocumentExpiryDate] = useState(new Date());
  const [
    isDocumentExpiryDateDisabled,
    setIsDocumentExpiryDateDisabled
  ] = useState(true);
  const [
    isDocumentIssueDateDisabled,
    setIsDocumentIssueDateDisabled
  ] = useState(true);
  const [validationErrors, setValidationErrors] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubtypeSelector, setShowSubtypeSelector] = useState(false);
  const [documentRef, setDocumentRef] = useState();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isDisplayDropzone, setIsDisplayDropzone] = useState(true);

  const handleIssueDateChange = ({ date }) => setDocumentIssueDate(date);
  const handleExpiryDateChange = ({ date }) => setDocumentExpiryDate(date);

  const auth = useAuth();
  const isAllowedToCreateMemberCompliance = auth.hasRole(
    Role.MEMBER_COMPLIANCE_CREATE
  );

  const resetUploader = () => {
    setDocumentType("");
    setDocumentSubtype("");

    // XXX can't reset RotaCalendar's value - needs fix in rota-design - RD-3894
    // setDocumentIssueDate(new Date());
    // setDocumentExpiryDate(new Date());

    setIsDocumentIssueDateDisabled(true);
    setIsDocumentExpiryDateDisabled(true);
    setValidationErrors(null);

    // Reset Dropzone by flickering it in and out of React component tree
    setIsDisplayDropzone(false);
    setIsDisplayDropzone(true);
  };

  const validateInputData = () => {
    const validationErrors = {};
    let isValid = true;
    if (!documentType) {
      validationErrors.documentType = "Please select Document type.";
    }

    if (documentType === DOCUMENT_TYPES.RIGHT_TO_WORK && !documentSubtype) {
      validationErrors.documentSubtype = "Please select Category.";
      isValid = false;
    }

    if (
      ![documentType, documentSubtype].some(document =>
        NO_ISSUE_DATE_NEEDED.includes(document)
      ) &&
      moment(documentIssueDate).isAfter(new Date())
    ) {
      validationErrors.documentIssueDate = "Issue date can't be in future.";
      isValid = false;
    }

    if (
      ![documentType, documentSubtype].some(document =>
        NO_EXPIRY_DATE_NEEDED.includes(document)
      ) &&
      moment(documentExpiryDate).isBefore(new Date())
    ) {
      validationErrors.documentExpiryDate = "Expiry date can't be in past.";
      isValid = false;
    }

    if (!documentRef.current.files.length) {
      validationErrors.file = "Please select document for upload.";
      isValid = false;
    }

    return isValid ? true : !!setValidationErrors(validationErrors);
  };

  const handleUploadButton = async () => {
    setIsLoading(true);

    if (validateInputData()) {
      await handleUploadFile(
        documentType,
        "complianceDocument",
        documentSubtype,
        isDocumentIssueDateDisabled ? undefined : documentIssueDate,
        isDocumentExpiryDateDisabled ? undefined : documentExpiryDate
      );
      resetUploader();
      setUploadPercentage(0);
    }

    setIsLoading(false);
  };

  const handleDocumentTypeChange = e => {
    const selectedValue = e.target.value;
    setDocumentType(selectedValue);
    setIsDocumentExpiryDateDisabled(
      NO_EXPIRY_DATE_NEEDED.includes(selectedValue)
    );
    setIsDocumentIssueDateDisabled(
      NO_ISSUE_DATE_NEEDED.includes(selectedValue)
    );
    if (selectedValue === DOCUMENT_TYPES.RIGHT_TO_WORK) {
      setShowSubtypeSelector(true);
    } else {
      setShowSubtypeSelector(false);
    }
    setDocumentSubtype("");
    setIsDisabled(false);
  };

  const handleDocumentSubtypeChange = e => {
    const selectedValue = e.target.value;
    setDocumentSubtype(selectedValue);
    setIsDocumentExpiryDateDisabled(
      NO_EXPIRY_DATE_NEEDED.includes(selectedValue)
    );
    setIsDocumentIssueDateDisabled(
      NO_ISSUE_DATE_NEEDED.includes(selectedValue)
    );
  };

  COMPLIANCE_DOCUMENT_TYPES.sort((a, b) =>
    a === DOCUMENT_TYPES.RIGHT_TO_WORK
      ? -1
      : b === DOCUMENT_TYPES.RIGHT_TO_WORK
      ? 1
      : a?.localeCompare(b)
  );

  /**
   * @description this function helps to return a valid date whether a calender is used or form input is used by user.
   * It is useful when shouldNotAllowInput of RotaCalendar component is set to false
   * @param {Object} event event object from DOM
   * @returns date object
   */
  const documentUploadForm = (
    <>
      <Grid item xs={5} className={classes.item}>
        <FormControl variant="outlined" className={classes.formControl}>
          <label className={classes.label}>Document type</label>
          <div className={classes.redBox}></div>
          <Select
            onChange={handleDocumentTypeChange}
            value={documentType}
            autoWidth={true}
            fullWidth={true}
            className={classes.select}
          >
            {COMPLIANCE_DOCUMENT_TYPES.map((type, index) => (
              <MenuItem value={type} key={index}>
                <StyledSentenceCase>
                  {DOCUMENT_TYPES_LABELS[type]}
                </StyledSentenceCase>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <StyledValidationError>
          {validationErrors?.documentType}
        </StyledValidationError>
      </Grid>
      <Grid item xs={2} />
      {showSubtypeSelector && (
        <Grid item xs={5} className={classes.item}>
          <FormControl variant="outlined" className={classes.formControl}>
            <label className={classes.label}>Category</label>
            <div className={classes.redBox}></div>
            <Select
              onChange={handleDocumentSubtypeChange}
              value={documentSubtype}
              autoWidth={true}
              fullWidth={true}
              className={classes.select}
            >
              {Object.keys(RIGHT_TO_WORK_SUBTYPES).map((subtype, index) => (
                <MenuItem value={subtype} key={index}>
                  {RIGHT_TO_WORK_SUBTYPES_LABELS[subtype]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <StyledValidationError>
            {validationErrors?.documentSubtype}
          </StyledValidationError>
        </Grid>
      )}
      {!showSubtypeSelector && <Grid item xs={5} />}

      <Grid item xs={5} className={`${classes.item}`}>
        <label className={classes.label}>Issue Date:</label>
        <div className={classes.redBox}></div>

        <RotaCalendar
          onChange={handleIssueDateChange}
          startDate={documentIssueDate}
          maxDate={new Date()}
          disabled={isDocumentIssueDateDisabled}
          maxYear={2022}
        />

        <StyledValidationError>
          {validationErrors?.documentIssueDate}
        </StyledValidationError>
      </Grid>
      <Grid item xs={2} />
      <Grid item xs={5} className={`${classes.item}`}>
        <label className={classes.label}>Expiry Date:</label>
        <div className={classes.redBox}></div>

        <RotaCalendar
          onChange={handleExpiryDateChange}
          startDate={documentExpiryDate}
          minDate={new Date()}
          shouldNotSelectPastDate={true}
          disabled={isDocumentExpiryDateDisabled}
          minYear={2000}
          maxYear={2035}
        />

        <StyledValidationError>
          {validationErrors?.documentExpiryDate}
        </StyledValidationError>
      </Grid>

      <Grid item xs={12} className={classes.item}>
        {isDisplayDropzone && (
          <CustomDropzone
            id="complianceDocument"
            progress={uploadPercentage}
            isDisabled={isDisabled}
            setDocumentRef={setDocumentRef}
          />
        )}
        <p className={classes.fileNote}>
          Max {MAX_UPLOAD_SIZE_MB} MB file size, formats accepted (jpeg, jpg,
          png, pdf)
        </p>
      </Grid>

      <Grid item xs={5} className={classes.item}>
        <Button
          className={
            isDisabled ? classes.uploadButtonDisabled : classes.uploadButton
          }
          variant="contained"
          color="primary"
          onClick={() => handleUploadButton()}
          fullWidth
          disabled={isDisabled || isLoading}
        >
          {isLoading && (
            <CircularProgress size={20} className={classes.spinner} />
          )}
          {!isLoading && "Upload"}
        </Button>
      </Grid>

      <Grid item xs={7} />
    </>
  );

  return (
    <>
      <Grid container>
        {(isAllowedToCreateMemberCompliance || complianceDocuments) && (
          <h2 className={classes.title}>Compliance Details</h2>
        )}
        {isAllowedToCreateMemberCompliance && (
          <>
            <Grid item xs={12}>
              <p className={classes.subtitle}>Documentation Uploader</p>
              {showSubtypeSelector && (
                <p className={classes.tooltip}>
                  A member's right to work will lock based on the{" "}
                  <strong>soonest</strong> expiry date. Once this has expired,
                  you will need to upload a new right to work document before
                  re-activating the account.
                </p>
              )}
            </Grid>
            {documentUploadForm}
          </>
        )}
        {complianceDocuments && (
          <Grid item xs={12} className={classes.item}>
            <ComplianceDocumentsTable
              complianceDocuments={complianceDocuments}
              deleteDocument={handleDeleteDocument}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default ComplianceDocuments;
